"use client";

import { useConsent } from "@shared/hooks/cookie-consent";
import {
	NEXT_THEMES_DEFAULT_THEME_STORAGE_KEY,
	PREFERENCES_THEME_LOCAL_STORAGE_KEY,
	readThemePreference,
} from "@shared/lib/consent/preferences-consent-storage";
import { ThemeProvider, useTheme } from "next-themes";
import {
	type PropsWithChildren,
	useEffect,
	useLayoutEffect,
	useRef,
} from "react";
import { config } from "@/config";

/**
 * next-themes always persists to `storageKey` on `setTheme`. When preferences consent is
 * off, strip that key after each update so theme stays in-memory only. Also remove
 * next-themes' legacy default key (`theme`) if it was ever written.
 */
function ThemeStorageConsentStrip({
	preferencesAllowed,
}: {
	preferencesAllowed: boolean;
}) {
	const { theme } = useTheme();

	// Re-run after `setTheme` so next-themes persistence can be stripped when the
	// preferences tier is off (Biome cannot infer that `theme` drives this).
	// Intentional deps: theme triggers post-setTheme localStorage cleanup
	useLayoutEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.localStorage.removeItem(
				NEXT_THEMES_DEFAULT_THEME_STORAGE_KEY,
			);
			if (!preferencesAllowed) {
				window.localStorage.removeItem(
					PREFERENCES_THEME_LOCAL_STORAGE_KEY,
				);
			}
		} catch {
			// Storage unavailable.
		}
	}, [preferencesAllowed, theme]);

	return null;
}

function ThemePreferenceHydrator({
	preferencesAllowed,
}: {
	preferencesAllowed: boolean;
}) {
	const { setTheme } = useTheme();
	const previousPreferencesAllowedRef = useRef(preferencesAllowed);

	useEffect(() => {
		if (previousPreferencesAllowedRef.current && !preferencesAllowed) {
			setTheme(config.defaultTheme);
		}
		if (!previousPreferencesAllowedRef.current && preferencesAllowed) {
			const storedTheme = readThemePreference();
			if (storedTheme != null) {
				setTheme(storedTheme);
			}
		}
		previousPreferencesAllowedRef.current = preferencesAllowed;
	}, [preferencesAllowed, setTheme]);

	return null;
}

export function ConsentAwareThemeProvider({ children }: PropsWithChildren) {
	const { hasConsent, status } = useConsent();
	const preferencesAllowed =
		status === "decided" && hasConsent("preferences");

	return (
		<ThemeProvider
			attribute="class"
			disableTransitionOnChange
			enableSystem
			defaultTheme={config.defaultTheme}
			themes={Array.from(config.enabledThemes)}
			storageKey={PREFERENCES_THEME_LOCAL_STORAGE_KEY}
		>
			<ThemeStorageConsentStrip preferencesAllowed={preferencesAllowed} />
			<ThemePreferenceHydrator preferencesAllowed={preferencesAllowed} />
			{children}
		</ThemeProvider>
	);
}
